import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usd } from "@/components/media/ModelPicker";
import { errMsg, useApi } from "@/lib/api";

const KEYS = [
  ["unsplash", "Unsplash · Access Key", "https://unsplash.com/developers", "unsplash_key"],
  ["pexels", "Pexels · API Key", "https://www.pexels.com/api/", "pexels_key"],
];

export default function MediaSettings() {
  const api = useApi();
  const [st, setSt] = useState(null);
  const [vals, setVals] = useState({ unsplash: "", pexels: "" });
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/media/providers").then(({ data }) => setSt(data)).catch(() => setSt(false)); }, [api]);
  const save = async (prov, field, value) => {
    setBusy(prov);
    try { const { data } = await api.put("/media/providers", { [field]: value }); setSt(data); setVals((v) => ({ ...v, [prov]: "" })); toast.success(value ? "Chiave verificata e salvata (cifrata)" : "Chiave rimossa"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  if (!st) return <p className="text-sm text-slate-500">{st === false ? "Stato non disponibile" : "Caricamento…"}</p>;
  return (
    <div className="space-y-5" data-testid="media-settings">
      <p className="text-sm text-slate-600" data-testid="media-active-source">
        Fonte attiva per le foto stock: <strong>{st.active === "internal" ? `Libreria interna verificata (${st.internal_count} foto)` : st.stock[st.active].label}</strong>.
        {" "}Le chiavi sono facoltative e gratuite: Unsplash ha la precedenza, Pexels è la riserva.
      </p>
      {KEYS.map(([prov, label, link, field]) => (
        <div key={prov} className="grid sm:grid-cols-[200px_1fr] gap-2 sm:items-center">
          <p className="text-sm text-slate-600">{label}<a href={link} target="_blank" rel="noreferrer" className="block text-[11px] text-sky-700 hover:underline">Ottieni la chiave</a></p>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="password" value={vals[prov]} onChange={(e) => setVals({ ...vals, [prov]: e.target.value })} className="max-w-xs"
              placeholder={st.stock[prov].configured ? `Configurata ${st.stock[prov].hint}` : "Incolla la chiave"} data-testid={`media-key-${prov}`} />
            <Button size="sm" variant="outline" onClick={() => save(prov, field, vals[prov].trim())} disabled={!vals[prov].trim() || !!busy} data-testid={`media-key-save-${prov}`}>
              {busy === prov ? <Loader2 size={14} className="animate-spin" /> : "Verifica e salva"}
            </Button>
            {st.stock[prov].configured && (
              <>
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700" data-testid={`media-key-ok-${prov}`}><CheckCircle2 size={14} />Attiva</span>
                <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => save(prov, field, "")} disabled={!!busy} data-testid={`media-key-remove-${prov}`}><Trash2 size={14} /></Button>
              </>
            )}
          </div>
        </div>
      ))}
      <div>
        <p className="text-sm font-medium text-slate-800">Modelli per le immagini AI (Universal Key)</p>
        <ul className="mt-2 grid gap-2 sm:grid-cols-3" data-testid="media-image-models">
          {st.image_models.map((m) => (
            <li key={m.id} className="rounded-lg border p-3">
              <p className="flex justify-between text-sm font-medium text-slate-900">{m.label}<span className="tabular-nums">{usd(m.usd)}</span></p>
              <p className="mt-1 text-[11px] text-slate-500">{m.note}{m.default ? " · predefinito" : ""}</p>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[11px] text-slate-500">Prezzo indicativo per immagine. Le immagini AI vengono generate solo dopo la conferma esplicita del costo (max {st.max_ai_images} per operazione).</p>
      </div>
    </div>
  );
}
